import React, { useCallback, useEffect, useState } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import toastr from 'toastr';
import moment from 'moment';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { Card } from 'ktm-ui';
import CustomPagination from 'components/CustomPagination';
import ConfirmActionModal from 'components/ConfirmActionModal';
import AddFirmUserModal from './AddFirmUserModal';
import ChangeAccessTypeModal from './ChangeAccessTypeModal';
import RevokeFirmAccessModal from './RevokeFirmAccessModal';
import { describeLogEntry, UserAccessLogEntry } from './firmUserAccessLog';
import { formatFirmName } from './firmName';
import AdvisorViewLink from '../AdvisorViewLink';
import '../../styles/CustomComponents.css';
import '../../styles/Employee.css';

export type FirmUser = {
  id: string | null;
  firstName: string;
  lastName: string;
  email: string;
  isOwner: boolean;
  isClientOnly: boolean;
  isAdvisor: boolean;
  accessTypeLabel: string;
};

type Distro = { id: string; name: string; healthClaimDistroId: string };

type Props = { distro: Distro; userList: string[]; onBack: () => void };

const USERS_PER_PAGE = 10;
const LOG_PER_PAGE = 8;

const accessTagStyle: React.CSSProperties = {
  display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 600,
  whiteSpace: 'nowrap', background: '#E0F2FE', color: '#075985', border: '0.5px solid #7DD3FC',
};

const centeredMessageStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80,
};

const headerButtonStyle: React.CSSProperties = {
  display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 6,
  height: 30, padding: '0 12px', whiteSpace: 'nowrap', width: 'auto',
};

const paginationStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, marginBottom: 10,
};

const pageSlice = <T,>(items: T[], page: number, perPage: number) => {
  const last = page * perPage;
  const first = last - perPage;
  return { rows: items.slice(first, last), first, last: Math.min(last, items.length), pages: Math.ceil(items.length / perPage) };
};

const FirmUserAccessPage = ({ distro, userList, onBack }: Props) => {
  const [users, setUsers] = useState<FirmUser[]>([]);
  const [log, setLog] = useState<UserAccessLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersPage, setUsersPage] = useState(1);
  const [logPage, setLogPage] = useState(1);
  const [showLog, setShowLog] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [changeUser, setChangeUser] = useState<FirmUser | null>(null);
  const [revokeUser, setRevokeUser] = useState<FirmUser | null>(null);
  const [welcomeUser, setWelcomeUser] = useState<FirmUser | null>(null);

  const load = useCallback(async () => {
    try {
      const [usersRes, logRes] = await Promise.all([
        axios.get('/api/Advisors/getdistroaccountusers', { params: { distroAccountId: distro.id } }),
        axios.get(`/api/Advisors/distro/${distro.id}/useraccesslog`),
      ]);
      setUsers(usersRes.data || []);
      setLog(logRes.data || []);
      setLogPage(1);
    } catch {
      toastr.error('Failed to load firm users.');
    } finally {
      setLoading(false);
    }
  }, [distro.id]);

  useEffect(() => { load(); }, [load]);

  const closeAndMaybeReload = (setter: (v: any) => void) => (changed: boolean) => {
    setter(null);
    if (changed) load();
  };

  const sendWelcome = async () => {
    const user = welcomeUser;
    setWelcomeUser(null);
    if (!user) return;
    try {
      await axios.post(`/api/Advisors/distro/${distro.id}/sendwelcome`, { email: user.email });
      toastr.success(`Sent welcome email to ${user.email}.`);
      load();
    } catch {
      toastr.error(`Failed to send welcome email to ${user.email}.`);
    }
  };

  const actionLink = (label: string, onClick: () => void, danger = false) => (
    <a
      href="#"
      className="RT-Link"
      style={danger ? { color: 'red' } : undefined}
      onClick={e => { e.preventDefault(); onClick(); }}
    >
      {label}
    </a>
  );

  const renderActions = (u: FirmUser) => {
    const links = [actionLink('Send Welcome', () => setWelcomeUser(u))];
    if (u.id && (!u.isOwner || u.isClientOnly)) links.push(actionLink('Change Access Type', () => setChangeUser(u)));
    if (u.id && !u.isOwner) links.push(actionLink('Remove', () => setRevokeUser(u), true));
    return links.map((link, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className="pa-action-sep">|</span>}
        {link}
      </React.Fragment>
    ));
  };

  const usersView = pageSlice(users, usersPage, USERS_PER_PAGE);
  const logView = pageSlice(log, logPage, LOG_PER_PAGE);

  let usersContent;
  if (loading) {
    usersContent = <div style={{ ...centeredMessageStyle, height: 200 }}><Spinner animation="border" variant="primary" /></div>;
  } else if (users.length === 0) {
    usersContent = <div style={{ ...centeredMessageStyle, height: 200 }}><span style={{ fontWeight: 'bold' }}>No users have access to this firm.</span></div>;
  } else {
    usersContent = (
      <>
        <Table hover className="ktm-table pa-table" style={{ backgroundColor: 'white', width: '100%', marginBottom: 0 }}>
          <thead>
            <tr>
              <th>Last Name</th>
              <th>First Name</th>
              <th className="pa-col-email">Email</th>
              <th>Access Type</th>
              <th>Enter Portal</th>
              <th className="pa-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {usersView.rows.map(u => (
              <tr key={u.id ?? `owner-${u.email}`}>
                <td>{u.lastName}</td>
                <td>{u.firstName}</td>
                <td className="pa-col-email"><span className="pa-email" title={u.email}>{u.email}</span></td>
                <td><span style={accessTagStyle}>{u.accessTypeLabel}</span></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {u.id && (
                    <AdvisorViewLink target={{ distroAccountUserId: u.id }} title={`View the advisor portal as ${u.email}`}>
                      View as User
                    </AdvisorViewLink>
                  )}
                </td>
                <td className="pa-col-actions">{renderActions(u)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {usersView.pages > 1 && (
          <div className="pagination-container" style={paginationStyle}>
            <div className="pagination-info">
              <span>Showing {usersView.first + 1} to {usersView.last} of {users.length} entries</span>
            </div>
            <CustomPagination nPages={usersView.pages} currentPage={usersPage} setCurrentPage={setUsersPage} />
          </div>
        )}
      </>
    );
  }

  const historyContent = log.length === 0 ? (
    <div style={centeredMessageStyle}><span style={{ fontWeight: 'bold' }}>No user access changes recorded yet.</span></div>
  ) : (
    <div className="history-content">
      <Table hover className="ktm-table pa-table" style={{ backgroundColor: 'white', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 170 }}>Date</th>
            <th style={{ width: 250 }}>User</th>
            <th style={{ width: 170 }}>Action</th>
            <th>Change</th>
            <th style={{ width: 170 }}>Changed By</th>
          </tr>
        </thead>
        <tbody>
          {logView.rows.map(entry => {
            const d = describeLogEntry(entry);
            return (
              <tr key={entry.id}>
                <td>{moment(entry.createdAt).format('YYYY-MM-DD HH:mm:ss')}</td>
                <td>{entry.targetEmail}</td>
                <td>{d.action}</td>
                <td>{d.details}</td>
                <td>{entry.performedBy ?? ''}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {logView.pages > 1 && (
        <div className="pagination-container" style={paginationStyle}>
          <div className="pagination-info">
            <span>Showing {logView.first + 1} to {logView.last} of {log.length} entries</span>
          </div>
          <CustomPagination nPages={logView.pages} currentPage={logPage} setCurrentPage={setLogPage} />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="RT-Button RT-Button--cancel" onClick={onBack} style={headerButtonStyle}>
            <ArrowLeft size={14} style={{ display: 'inline-block', flexShrink: 0, marginRight: 0 }} />
            <span>Back</span>
          </button>
          <span style={{ fontSize: 16, fontWeight: 600 }}>User Access</span>
          <span className="text-muted" style={{ fontSize: 14 }}>{formatFirmName(distro)}</span>
        </div>
        <button className="RT-Button RT-Button--primary" onClick={() => setShowAdd(true)} style={headerButtonStyle}>
          <UserPlus size={14} style={{ display: 'inline-block', flexShrink: 0 }} />
          <span>Add User</span>
        </button>
      </div>

      {usersContent}

      <div style={{ marginTop: 8 }}>
        <Card
          label="User Access History"
          actions={
            <button onClick={() => setShowLog(v => !v)} className={`toggle-button ${showLog ? 'open' : ''}`}>
              <span className="arrow"></span>
            </button>
          }
        >
          {showLog && historyContent}
        </Card>
      </div>

      {showAdd && <AddFirmUserModal distro={distro} userList={userList} onClose={changed => { setShowAdd(false); if (changed) load(); }} />}
      {changeUser && <ChangeAccessTypeModal distro={distro} user={changeUser} onClose={closeAndMaybeReload(setChangeUser)} />}
      {revokeUser && <RevokeFirmAccessModal distro={distro} user={revokeUser} onClose={closeAndMaybeReload(setRevokeUser)} />}
      {welcomeUser && (
        <ConfirmActionModal
          title="Send welcome email"
          message={`This will immediately send the welcome email to ${welcomeUser.email}. Would you like to proceed?`}
          confirmLabel="Send Email"
          onConfirm={sendWelcome}
          onCancel={() => setWelcomeUser(null)}
        />
      )}
    </div>
  );
};

export default FirmUserAccessPage;
